import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { useTRPC } from "@/integrations/trpc/react";
import { Button } from "@/components/ui/button";
import {
	SolicitationHeader,
	SolicitationKeyDates,
	SolicitationDocuments,
	SolicitationMilestones,
	SolicitationContactInfo,
	SolicitationSubmitBidCta,
} from "@/components/solicitation";
import { getEffectiveStatus } from "@/lib/utils";
import { SolicitationQA } from "@/components/solicitation/SolicitationQA";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute(
	"/vendor/$buyerSlug/solicitations/$solicitationId",
)({
	component: VendorSolicitationDetailPage,
});

function VendorSolicitationDetailPage() {
	const { buyerSlug, solicitationId } = Route.useParams();
	const buyer = useBuyerContext();
	const trpc = useTRPC();
	const { user } = useAuth();

	// Fetch solicitation details
	const {
		data: solicitation,
		isLoading: solicitationLoading,
		error: solicitationError,
	} = useQuery(
		trpc.public.solicitations.get.queryOptions({
			solicitationId,
		}),
	);

	// Fetch attachments (including archived)
	const { data: attachmentsData } = useQuery(
		trpc.public.solicitationDetails.attachments.queryOptions({
			solicitationId,
		}),
	);
	const attachments = attachmentsData?.attachments ?? [];
	const archivedAttachments = attachmentsData?.archivedAttachments ?? [];

	// Fetch forms (including archived) with PDF download URLs
	const { data: formsData } = useQuery(
		trpc.public.solicitationDetails.forms.queryOptions({
			solicitationId,
		}),
	);
	const forms = formsData?.forms ?? [];
	const archivedForms = formsData?.archivedForms ?? [];

	// Fetch milestones
	const { data: milestones } = useQuery(
		trpc.public.solicitationDetails.milestones.queryOptions({
			solicitationId,
		}),
	);

	if (solicitationLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	if (solicitationError || !solicitation) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="space-y-6">
					<Button variant="ghost" asChild>
						<Link
							to="/vendor/$buyerSlug/solicitations"
							params={{ buyerSlug }}
						>
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Solicitations
						</Link>
					</Button>
					<div className="text-center py-12">
						<h2 className="text-2xl font-bold">Solicitation not found</h2>
						<p className="text-muted-foreground mt-2">
							This solicitation is not available.
						</p>
						<Button asChild className="mt-4">
							<Link
								to="/vendor/$buyerSlug/solicitations"
								params={{ buyerSlug }}
							>
								View All Solicitations
							</Link>
						</Button>
					</div>
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Back button */}
				<Button variant="ghost" asChild>
					<Link to="/vendor/$buyerSlug/solicitations" params={{ buyerSlug }}>
						<ArrowLeft className="mr-2 h-4 w-4" />
						Back to Solicitations
					</Link>
				</Button>

				{/* Header */}
				<SolicitationHeader
					title={solicitation.title ?? "Untitled Solicitation"}
					description={solicitation.description}
					type={solicitation.type}
					status={getEffectiveStatus(solicitation.status, solicitation.publish_date, solicitation.due_date)}
				/>

				{/* Key Dates */}
				<SolicitationKeyDates
					dueDate={solicitation.due_date}
					publishDate={solicitation.publish_date}
					documentCount={attachments.length}
				/>

				{/* Main content grid */}
				<div className="grid gap-6 md:grid-cols-3">
					<div className="md:col-span-2 space-y-6">
						{/* Documents & Forms */}
						<SolicitationDocuments
							attachments={attachments}
							archivedAttachments={archivedAttachments}
							forms={forms}
							archivedForms={archivedForms}
						/>

						{/* Milestones */}
						<SolicitationMilestones milestones={milestones ?? []} />

						{/* Q&A */}
						<SolicitationQA
							solicitationId={solicitationId}
							buyerOrgId={solicitation.buyer_org_id}
							mode="vendor"
							vendorEmail={user?.email ?? undefined}
						/>
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Submit Bid CTA */}
						<SolicitationSubmitBidCta
							solicitationId={solicitation.id}
							solicitationTitle={solicitation.title ?? undefined}
							buyerSlug={buyerSlug}
							status={getEffectiveStatus(solicitation.status, solicitation.publish_date, solicitation.due_date)}
							isAuthenticated={true}
						/>

						{/* Contact Info */}
						<SolicitationContactInfo
							name={buyer.name}
							email={buyer.email ?? null}
							phone={buyer.phone ?? null}
							address={buyer.address ?? null}
						/>
					</div>
				</div>
			</div>
		</VendorLayout>
	);
}
